import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Layers, Check } from "lucide-react";

/**
 * PhasePicker — inline picker that reads the project's phases and lets
 * the caller assign / clear a task's phase. Kept controlled from the
 * caller: `value` is the current phase_id (nullable), `onChange(next)`
 * is called with either a phase_id string or null (for Unphased).
 *
 * Used from:
 *  - buildTaskColumns → PHASE column render (inline cell)
 *  - Tasks.jsx TaskDetail dialog
 */
export default function PhasePicker({
  projectId,
  value,
  onChange,
  triggerClassName = "",
  size = "cell",   // "cell" | "field"
  testid = "phase-picker",
  disabled = false,
  // Iter Feb2026 · Fallback label + colour used before the picker has
  // fetched the project's phases. Callers should pass the hydrated
  // `phase_name` so cells don't briefly render "…" on first paint.
  fallbackLabel = null,
  fallbackColor = null,
}) {
  const [open, setOpen] = useState(false);
  const [phases, setPhases] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    setBusy(true);
    api.get(`/projects/${projectId}/phases`)
      .then((r) => setPhases(r.data.phases || []))
      .catch(() => setPhases([]))
      .finally(() => setBusy(false));
  }, [open, projectId]);

  const current = useMemo(
    () => phases.find((p) => p.id === value) || null,
    [phases, value],
  );

  const pick = async (phId) => {
    if (disabled) return;
    if ((phId || null) === (value || null)) { setOpen(false); return; }
    try {
      await onChange?.(phId || null);
      setOpen(false);
    } catch { /* caller shows toast */ }
  };

  const label = (() => {
    if (current?.name) return current.name;
    if (value && fallbackLabel) return fallbackLabel;
    if (value) return "…";  // loading
    return "Unphased";
  })();

  const dotColor = current?.color || (value ? fallbackColor : null);

  const wrapCls = size === "field"
    ? "inline-flex items-center gap-1 h-8 px-2 border border-border text-xs rounded-none w-full justify-start"
    : "inline-flex items-center gap-1 text-xs hover:bg-muted/40 px-1 py-0.5 -mx-1 -my-0.5 w-full text-left";

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild disabled={disabled || !projectId}>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={`${wrapCls} ${triggerClassName}`}
          data-testid={testid}
          title={projectId ? "Change phase" : "Assign a project first"}>
          {dotColor && (
            <span className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: dotColor }} />
          )}
          <span className={value ? "truncate" : "truncate text-[10px] uppercase tracking-widest text-muted-foreground italic"}>
            {label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="rounded-none w-56 p-0">
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest border-b border-border bg-muted/40 flex items-center gap-1">
          <Layers className="w-3 h-3" /> Phase
        </div>
        <div className="max-h-[240px] overflow-y-auto">
          <button
            type="button"
            onClick={() => pick(null)}
            className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted"
            data-testid={`${testid}-unphased`}>
            <span className="w-2 h-2 rounded-full bg-muted-foreground/30 shrink-0" />
            <span className="flex-1 truncate italic text-muted-foreground">Unphased</span>
            {!value && <Check className="w-3 h-3" />}
          </button>
          {busy && phases.length === 0 && (
            <div className="px-2 py-2 text-[10px] text-muted-foreground">Loading…</div>
          )}
          {phases.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => pick(p.id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted"
              data-testid={`${testid}-${p.id}`}>
              <span className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: p.color || "var(--border)" }} />
              <span className="flex-1 truncate">{p.name}</span>
              {(p.task_count != null) && (
                <span className="text-[10px] text-muted-foreground font-mono tabular-nums">
                  {p.task_count}
                </span>
              )}
              {value === p.id && <Check className="w-3 h-3" />}
            </button>
          ))}
          {!busy && phases.length === 0 && (
            <div className="px-2 py-2 text-[10px] text-muted-foreground">
              This project has no phases yet.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
